'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { fetchCareerOrganizationPlayersBySelection, fetchCareerOrganizations } from '@/lib/apiClient';
import type { CareerOrganizationMeta, CareerOrganizationPlayersResponse } from '@/lib/types';
import type { Locale } from '@/lib/localePath';
import { CAREER_CATEGORIES, categoryName, careerCategory, parseCareerRoles, organizationFilterHref, type CareerCategory, type CareerRoleFilters } from '@/lib/careerOrganizationFilters';
import CareerOrganizationPlayerTable from './CareerOrganizationPlayerTable';
import '@/styles/InfoPage.css';
import '@/styles/CareerOrganization.css';

export { organizationFilterHref } from '@/lib/careerOrganizationFilters';
const text = (locale: Locale, ja: string, en: string, es: string) => locale === 'ja' ? ja : locale === 'es' ? es : en;
const localizedName = (item: CareerOrganizationMeta, locale: Locale) => locale === 'ja' ? item.nameJa : locale === 'es' ? item.nameEs ?? item.nameEn ?? item.nameJa : item.nameEn ?? item.nameJa;

export default function CareerOrganizationFilterClient({ locale, primarySlug, initial }: {
  locale: Locale; primarySlug?: string; initial?: CareerOrganizationPlayersResponse;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const selected = useMemo(() => Array.from(new Set([...(primarySlug ? [primarySlug] : []), ...searchParams.getAll('organization')])).filter(Boolean), [primarySlug, searchParams]);
  const selectedKey = selected.join('\u0000');
  const roles = parseCareerRoles(searchParams.getAll('role'));
  const roleKey = JSON.stringify(roles);
  const requestKey = `${selectedKey}|${roleKey}`;
  const initialKey = initial ? `${initial.selectedOrganizationSlugs.join('\u0000')}|{}` : '';
  const [result, setResult] = useState<{ key: string; data: CareerOrganizationPlayersResponse | null }>({ key: initialKey, data: initial ?? null });
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<CareerOrganizationMeta[]>([]);
  const [searchFailed, setSearchFailed] = useState(false);
  const data = requestKey === initialKey ? initial : result.key === requestKey ? result.data : null;
  const pending = Boolean(selectedKey) && requestKey !== initialKey && result.key !== requestKey;
  const updateSelection = (next: string[], nextRoles: CareerRoleFilters = roles) =>
    router.replace(organizationFilterHref(locale, next, nextRoles), { scroll: false });
  const changeRole = (category: CareerCategory, role: 'player' | 'coaching', checked: boolean) => {
    const mode = roles[category];
    let player = mode === undefined || mode === 'player';
    let coaching = mode === undefined || mode === 'coaching';
    if (role === 'player') player = checked; else coaching = checked;
    const next = { ...roles };
    if (player && coaching) delete next[category];
    else next[category] = player ? 'player' : coaching ? 'coaching' : 'none';
    updateSelection(selected, next);
  };

  useEffect(() => {
    if (!selectedKey || requestKey === initialKey) return;
    const controller = new AbortController();
    fetchCareerOrganizationPlayersBySelection(selectedKey.split('\u0000'), controller.signal, JSON.parse(roleKey))
      .then((next) => {
        if (controller.signal.aborted) return;
        setResult({ key: requestKey, data: next });
        if (next.selectedOrganizationSlugs.join('\u0000') !== selectedKey)
          router.replace(organizationFilterHref(locale, next.selectedOrganizationSlugs, JSON.parse(roleKey)), { scroll: false });
      })
      .catch(() => { if (!controller.signal.aborted) setResult({ key: requestKey, data: null }); });
    return () => controller.abort();
  }, [selectedKey, roleKey, requestKey, initialKey, locale, router]);

  useEffect(() => {
    const query = search.trim();
    if (!query) { setSearchResults([]); setSearchFailed(false); return; }
    const controller = new AbortController();
    setSearchResults([]);
    fetchCareerOrganizations(query, controller.signal)
      .then((results) => { if (!controller.signal.aborted) { setSearchResults(results); setSearchFailed(false); } })
      .catch(() => { if (!controller.signal.aborted) { setSearchResults([]); setSearchFailed(true); } });
    return () => controller.abort();
  }, [search]);

  const selectedOrganizations = result.data?.selectedOrganizations ?? initial?.selectedOrganizations ?? [];
  const candidates = data?.availableOrganizations ?? [];
  const organization = initial?.organization;
  return <section className="info-page career-organization-page">
    <h1 className="career-organization-title">{organization ? localizedName(organization, locale) : text(locale, '経歴', 'Career', 'Trayectoria')}</h1>
    <div className="career-organization-controls">
      {selected.length === 0 ? <>
        <label htmlFor="career-organization-search">{text(locale, '経歴', 'Career', 'Trayectoria')}</label>
        <input id="career-organization-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={text(locale, '経歴を検索', 'Search careers', 'Buscar trayectorias')} />
        {searchFailed && <p className="career-organization-error">{text(locale, '経歴を取得できませんでした。', 'Careers could not be loaded.', 'No se pudieron cargar las trayectorias.')}</p>}
        {searchResults.length > 0 && <ul className="career-organization-search-results">{searchResults.map((item) => <li key={item.slug}><button type="button" onClick={() => updateSelection([item.slug])}>{localizedName(item, locale)}</button></li>)}</ul>}
      </> : <>
        <span>{text(locale, '経歴を追加', 'Add career', 'Añadir trayectoria')}</span>
        <div className="career-category-rows">
          {CAREER_CATEGORIES.map((category) => <div className="career-category-row" key={category}>
            <label htmlFor={`career-add-${category}`}>{categoryName(category, locale)}</label>
            <select id={`career-add-${category}`} value="" disabled={pending} onChange={(event) => event.target.value && updateSelection([...selected, event.target.value])}>
              <option value="">{text(locale, '選択', 'Select', 'Seleccionar')}</option>
              {candidates.filter((item) => careerCategory(item) === category).map((item) => <option key={item.slug} value={item.slug}>{localizedName(item, locale)}</option>)}
            </select>
            <div className="career-role-options" role="group" aria-label={categoryName(category, locale)}>
              {(['player', 'coaching'] as const).map((role) => <label className="career-role-option" key={role}>
                <input type="checkbox" checked={roles[category] === undefined || roles[category] === role} onChange={(event) => changeRole(category, role, event.target.checked)} />
                {role === 'player' ? text(locale, '選手', 'Players', 'Jugadores') : text(locale, '監督・コーチ', 'Managers/coaches', 'Mánagers/entrenadores')}
              </label>)}
            </div>
          </div>)}
        </div>
        <p className="career-role-note">{text(locale, '両方チェックすると役割未判定の経歴も含みます。', 'Both checked includes careers with an unknown role.', 'Ambas opciones incluyen trayectorias con rol desconocido.')}</p>
        <ul className="career-organization-selected" aria-label={text(locale, '選択した経歴', 'Selected careers', 'Trayectorias seleccionadas')}>
          {selected.map((slug) => { const item = selectedOrganizations.find((candidate) => candidate.slug === slug); return <li key={slug}><span>{item ? localizedName(item, locale) : '…'}</span><button type="button" onClick={() => updateSelection(selected.filter((value) => value !== slug))}>{text(locale, '削除', 'Remove', 'Quitar')}</button></li>; })}
        </ul>
      </>}
    </div>
    {selected.length > 0 && <section className="career-organization-players" aria-busy={pending}>
      <h2>{text(locale, '選手', 'Players', 'Jugadores')}</h2>
      {pending ? <p role="status">{text(locale, '読み込み中…', 'Loading…', 'Cargando…')}</p> : data ? <CareerOrganizationPlayerTable locale={locale} players={data.players} /> : <p className="career-organization-error">{text(locale, '選手を取得できませんでした。', 'Players could not be loaded.', 'No se pudieron cargar los jugadores.')}</p>}
    </section>}
  </section>;
}
